import React, { useState, useEffect } from 'react';
import { Partner, CreatePartnerRequest, UpdatePartnerRequest, PartnerType, PartnerTypeLabels, PartnerTypeColors } from '../types/partners';
import { partnersApi } from '../services/partnersApi';
import PartnerForm from '../components/partners/PartnerForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Toast from '../components/common/Toast';
import { Plus, Pencil, Trash2, Pause, Play, LayoutGrid, List, Users2, Mail, Phone, Globe, MapPin } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

const Partners: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; partner: Partner | null }>({
    isOpen: false,
    partner: null
  });

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadPartners = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnersApi.getAll(includeInactive);
      setPartners(data);
    } catch (err) {
      console.error('Error loading partners:', err);
      setError('Fehler beim Laden der Partner');
      showToast('Fehler beim Laden der Partner', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
  }, [includeInactive]);

  // CRUD operations
  const handleCreatePartner = async (partnerData: CreatePartnerRequest) => {
    try {
      setIsFormLoading(true);
      const newPartner = await partnersApi.create(partnerData);
      setPartners(prev => [...prev, newPartner]);
      showToast('Partner erfolgreich erstellt', 'success');
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error creating partner:', err);
      showToast('Fehler beim Erstellen des Partners', 'error');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleUpdatePartner = async (partnerData: UpdatePartnerRequest) => {
    if (!editingPartner) return;

    try {
      setIsFormLoading(true);
      const updatedPartner = await partnersApi.update(editingPartner.id, partnerData);
      setPartners(prev => prev.map(p => p.id === editingPartner.id ? updatedPartner : p));
      showToast('Partner erfolgreich aktualisiert', 'success');
      setIsFormOpen(false);
      setEditingPartner(null);
    } catch (err) {
      console.error('Error updating partner:', err);
      showToast('Fehler beim Aktualisieren des Partners', 'error');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleDeletePartner = async (partner: Partner) => {
    try {
      await partnersApi.delete(partner.id);
      setPartners(prev => prev.filter(p => p.id !== partner.id));
      showToast('Partner erfolgreich gelöscht', 'success');
      setDeleteConfirm({ isOpen: false, partner: null });
    } catch (err) {
      console.error('Error deleting partner:', err);
      showToast('Fehler beim Löschen des Partners', 'error');
    }
  };

  const handleToggleActive = async (partner: Partner) => {
    try {
      const updatedPartner = await partnersApi.toggleActive(partner.id);
      setPartners(prev => prev.map(p => p.id === partner.id ? updatedPartner : p));
      showToast(`Partner ${updatedPartner.isActive ? 'aktiviert' : 'deaktiviert'}`, 'success');
    } catch (err) {
      console.error('Error toggling partner active status:', err);
      showToast('Fehler beim Ändern des Partner-Status', 'error');
    }
  };

  const filteredPartners = partners.filter(partner => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (partner.name && partner.name.toLowerCase().includes(searchLower)) ||
                         (partner.description && partner.description.toLowerCase().includes(searchLower)) ||
                         (partner.contactEmail && partner.contactEmail.toLowerCase().includes(searchLower)) ||
                         (partner.address && partner.address.toLowerCase().includes(searchLower));
    const matchesType = filterType === 'all' || partner.partnerType === filterType;
    return matchesSearch && matchesType;
  });

  const getTypeColor = (type: string) => {
    return PartnerTypeColors[type as PartnerType] || '#6c757d';
  };

  const getTypeText = (type: string) => {
    return PartnerTypeLabels[type as PartnerType] || type;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Lade Partner...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler beim Laden der Partner</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => { setEditingPartner(null); setIsFormOpen(true); }}>
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
            Inaktive anzeigen
          </label>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + Filters + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Partner durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="page-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Alle Typen</option>
            {Object.entries(PartnerTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredPartners.length} / {partners.length} Partner</span>
        </div>
      </div>

      {filteredPartners.length === 0 ? (
        <div className="page-empty-state">
          <h3>Keine Partner gefunden</h3>
          <p>Es wurden keine Partner gefunden, die Ihren Suchkriterien entsprechen.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredPartners.map((partner) => (
            <div key={partner.id} className="page-card">
              <div className="page-card-header">
                <h3>{partner.name}</h3>
                <div className="page-card-badges">
                  <span className="page-badge" style={{ backgroundColor: getTypeColor(partner.partnerType) }}>
                    {getTypeText(partner.partnerType)}
                  </span>
                  <span className="page-badge" style={{ backgroundColor: partner.isActive ? '#28a745' : '#dc3545' }}>
                    {partner.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              {partner.description && (
                <p className="page-card-description">{partner.description}</p>
              )}
              <div className="page-card-contact">
                {partner.contactEmail && (
                  <div className="page-card-contact-item">
                    <Mail size={14} />
                    <a href={`mailto:${partner.contactEmail}`}>{partner.contactEmail}</a>
                  </div>
                )}
                {partner.contactPhone && (
                  <div className="page-card-contact-item">
                    <Phone size={14} />
                    <a href={`tel:${partner.contactPhone}`}>{partner.contactPhone}</a>
                  </div>
                )}
                {partner.website && (
                  <div className="page-card-contact-item">
                    <Globe size={14} />
                    <a href={partner.website} target="_blank" rel="noopener noreferrer">Website</a>
                  </div>
                )}
                {partner.address && (
                  <div className="page-card-contact-item">
                    <MapPin size={14} />
                    <span>{partner.address}</span>
                  </div>
                )}
              </div>
              <div className="page-card-footer">
                <div className="page-card-actions">
                  <button className="page-btn-icon edit" onClick={() => { setEditingPartner(partner); setIsFormOpen(true); }} title="Partner bearbeiten">
                    <Pencil size={14} />
                  </button>
                  <button className="page-btn-icon" style={{ backgroundColor: partner.isActive ? '#ffc107' : '#28a745', color: 'white' }} onClick={() => handleToggleActive(partner)} title={partner.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                    {partner.isActive ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <button className="page-btn-icon delete" onClick={() => setDeleteConfirm({ isOpen: true, partner })} title="Partner löschen">
                    <Trash2 size={14} />
                  </button>
                </div>
                <small>Erstellt: {new Date(partner.createdAt).toLocaleDateString('de-DE')}</small>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Typ</th>
                <th>Beschreibung</th>
                <th>Kontakt</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.map((partner) => (
                <tr key={partner.id}>
                  <td>{partner.name}</td>
                  <td>
                    <span className="page-badge" style={{ backgroundColor: getTypeColor(partner.partnerType) }}>
                      {getTypeText(partner.partnerType)}
                    </span>
                  </td>
                  <td>{partner.description || '-'}</td>
                  <td>
                    <div className="page-table-actions">
                      {partner.contactEmail && <a href={`mailto:${partner.contactEmail}`}><Mail size={14} /></a>}
                      {partner.contactPhone && <a href={`tel:${partner.contactPhone}`}><Phone size={14} /></a>}
                      {partner.website && <a href={partner.website} target="_blank" rel="noopener noreferrer"><Globe size={14} /></a>}
                    </div>
                  </td>
                  <td>
                    <span className="page-badge" style={{ backgroundColor: partner.isActive ? '#28a745' : '#dc3545' }}>
                      {partner.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{new Date(partner.createdAt).toLocaleDateString('de-DE')}</td>
                  <td>
                    <div className="page-table-actions">
                      <button className="page-btn-icon edit" onClick={() => { setEditingPartner(partner); setIsFormOpen(true); }} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon" style={{ backgroundColor: partner.isActive ? '#ffc107' : '#28a745', color: 'white' }} onClick={() => handleToggleActive(partner)} title={partner.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                        {partner.isActive ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button className="page-btn-icon delete" onClick={() => setDeleteConfirm({ isOpen: true, partner })} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Partner Form Modal */}
      {isFormOpen && (
        <PartnerForm
          partner={editingPartner || undefined}
          onSave={editingPartner ?
            (data: CreatePartnerRequest | UpdatePartnerRequest) => handleUpdatePartner(data as UpdatePartnerRequest) :
            (data: CreatePartnerRequest | UpdatePartnerRequest) => handleCreatePartner(data as CreatePartnerRequest)
          }
          onCancel={() => {
            setIsFormOpen(false);
            setEditingPartner(null);
          }}
          loading={isFormLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Partner löschen"
        message={`Möchten Sie den Partner "${deleteConfirm.partner?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        onConfirm={() => deleteConfirm.partner && handleDeletePartner(deleteConfirm.partner)}
        onCancel={() => setDeleteConfirm({ isOpen: false, partner: null })}
        type="danger"
      />

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Partners;
